import React from "react";
import { graphql, useFragment } from "react-relay";

import { faAlignLeft, faSparkles } from "@awesome.me/kit-4c83d50d0d/icons/classic/solid";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import cx from "classnames";

import { useMeetingRecorderStatus } from "~/meetingRecorder/utils";

import type { MeetingContainerTabs_note$key } from "~/__generated__/MeetingContainerTabs_note.graphql";

import styles from "../css/MeetingContainer.module.css";

export type CurrentTabType = "notes" | "recap";

type MeetingContainerTabsProps = {
	currentTab: CurrentTabType;
	setCurrentTab: (tab: CurrentTabType) => void;
	noteKey: MeetingContainerTabs_note$key | null;
	className?: string;
};

export default function MeetingContainerTabs({
	currentTab,
	setCurrentTab,
	className,
	noteKey,
}: MeetingContainerTabsProps) {
	const onNotesClick = () => setCurrentTab("notes");
	const onRecapClick = () => setCurrentTab("recap");

	const note = useFragment(
		graphql`
			fragment MeetingContainerTabs_note on Note {
				hasMeetingRecordings
				meetingRecordings {
					isCurrentlyRecording
				}
				...utils_useMeetingRecorderStatus_note
			}
		`,
		noteKey,
	);

	const { hasMeetingRecordings, meetingRecordings } = note ?? {};

	// The current note may have a meetingRecording record because a recording is in progress,
	// but we don't want to show the Recap tab until transcription has finished
	const recorderStatus = useMeetingRecorderStatus(note);
	const showRecapTab = React.useMemo(() => {
		const isSingleRecording = hasMeetingRecordings && meetingRecordings?.length === 1;
		if (isSingleRecording) {
			const singleRecordingIsInProgress =
				meetingRecordings.every(recording => recording.isCurrentlyRecording) ||
				(recorderStatus.status &&
					["IN_WAITING_ROOM", "JOINED_CALL", "RECORDING", "SCHEDULED"].includes(recorderStatus.status));
			return !singleRecordingIsInProgress;
		}

		return hasMeetingRecordings;
	}, [hasMeetingRecordings, meetingRecordings, recorderStatus.status]);

	React.useEffect(() => {
		if (!showRecapTab) {
			setCurrentTab("notes");
		}
	}, [setCurrentTab, showRecapTab]);

	if (!showRecapTab) {
		return null; // This isn't ideal but we can't call the hook in the NoteCard parent class
	}

	return (
		<div className={cx(styles.tabsWrapper, className)}>
			<div className={cx(styles.tab, { [styles.active]: currentTab === "notes" })} onClick={onNotesClick}>
				<FontAwesomeIcon fixedWidth icon={faAlignLeft} />
				Notes
			</div>

			<div
				className={cx(styles.tab, { [styles.active]: currentTab === "recap" })}
				onClick={onRecapClick}
				data-tour-tooltip-target="recap-tab"
			>
				<FontAwesomeIcon fixedWidth icon={faSparkles} />
				Recap
			</div>
		</div>
	);
}
